// When too many marks land on top of one another, draw the count instead.
//
// Its own module, importing nothing, for the reason pin-layout.ts and
// tap-target.ts give at the top of theirs: map.ts pulls in maplibre-gl and a
// stylesheet, neither of which survives a test runner, and this is arithmetic
// worth testing.
//
// ## Why not MapLibre's own clustering
//
// `cluster: true` on a GeoJSON source is one line, and #60 proposed exactly
// that. It cannot be used here, for a reason particular to this map:
// clustering is a property of the **source**, and every incident this map
// draws — power cuts, river gauges, cameras, forecourts, cones — arrives in
// the one `incidents` source that six layers read through six different
// filters. Switching it on there would bundle a power cut with a roadworks
// into one bubble, replace both with a cluster stub carrying none of the
// properties the popup paths read, and hand `queryRenderedFeatures` those
// stubs where stackHtml expects features.
//
// Giving the cones a clustered source of their own instead costs a second
// copy of the sprite layer, its filter, its sizing, its sort key and its click
// handler — five things to keep in step, which is the duplication
// REPLACES_CIRCLE's own history in map-style.ts is a warning about.
//
// So the grouping happens here, on the feature list, before it reaches the
// source. Members of a cluster are withheld from the incident collection and
// one bubble per group is published to a small source of its own. Every
// existing layer, filter and popup path then sees exactly what it saw before,
// less the features that are no longer drawn — and a cluster bubble, living in
// a source no incident layer reads, cannot collide with any of it.

/**
 * Sprites dense enough to be worth collapsing.
 *
 * Keyed on the sprite rather than on the provider, the rule REPLACES_CIRCLE
 * states and gives its reasons for: a provider id is a deployment detail, and
 * a layer added later that picks an existing sprite should inherit the
 * behaviour without anyone remembering to come here. Local roadworks (the
 * Scottish Road Works Register, 2,257 works held across the country) and
 * trunk roadworks (Traffic Scotland) both draw `roadworks`, so one entry
 * covers both.
 *
 * `road-closed` is deliberately absent. A closure is the one thing on this
 * layer worth seeing from a height — it changes the route rather than slowing
 * it — so it stays a mark of its own however many cones collapse around it.
 */
export const CLUSTERED_ICONS = [
  'roadworks', 'roadworks-future',
  /*
   * The sewer overflows, one entry per state.
   *
   * Scottish Water publishes four — overflowing now, overflowed in the last
   * 48 hours, no overflows, no data — and each already has a sprite of its
   * own, because the disc's colour is the whole message on that layer. So
   * "cluster the greens together and the reds together" needs no rule of its
   * own here: grouping is per sprite, and the state *is* the sprite.
   *
   * That is the second thing the sprite-keyed rule has now paid for, after
   * local and trunk roadworks sharing one entry. A rule keyed on the provider
   * would have had to learn what a state was.
   */
  'cso-not-discharging', 'cso-recent', 'cso-discharging', 'cso-no-data',
] as const;

/** Does this sprite collapse into a count when it crowds? */
export function clusters(icon: string | undefined): boolean {
  return CLUSTERED_ICONS.includes(icon as typeof CLUSTERED_ICONS[number]);
}

/**
 * The sprite a cluster of these draws.
 *
 * Derived rather than listed, so a sprite added to CLUSTERED_ICONS needs one
 * drawing in icons.ts and nothing else — the same argument the festival stars
 * and the air quality pins make there against near-identical entries in three
 * files. A name with no sprite behind it draws nothing at all and logs one
 * line, so scripts/test-map-clusters.mjs checks the drawing exists for every
 * icon that can reach this.
 */
export function clusterSpriteFor(icon: string): string {
  return `${icon}-cluster`;
}

/**
 * How close two marks must be to be counted together, in pixels.
 *
 * MapLibre's own `clusterRadius` default, which is as good a starting number
 * as any and has the advantage of being one somebody else has already argued
 * about. The roadworks sprite draws at about 30px across at city zoom, so a
 * 50px radius groups marks that would have overlapped and leaves marks that
 * would not have.
 */
export const CLUSTER_RADIUS_PX = 50;

/**
 * At and above this zoom nothing clusters: every mark draws as itself.
 *
 * 14 is the zoom the bus stop circles already appear at — see STOP_MIN_ZOOM in
 * map.ts — which is this map's existing answer to "close enough to be looking
 * at streets rather than at a city". Reusing it means one number rather than
 * two that will drift.
 *
 * It is also what stops a cluster of works at one coordinate becoming a trap.
 * Their bounds have no extent, so a click that fits them can only zoom in
 * blind, and if they clustered at every zoom there would be no zoom at which
 * the reader could reach them. Above the cut-off they draw stacked, and the
 * circle handler's stack join — the whole point of stackHtml — puts all of
 * them in one card.
 */
export const CLUSTER_MAX_ZOOM = 14;

/**
 * The fewest marks worth replacing with a number.
 *
 * MapLibre's own clustering has no minimum and will happily draw a bubble
 * reading "2" in the space the two marks it replaced would have fitted in,
 * which trades a thing you can click for a thing you must click twice. Three
 * is where the count starts saying something the marks did not.
 */
export const CLUSTER_MIN = 3;

/** Web Mercator's world square, in pixels, at zoom 0 — MapLibre's 512px tile. */
const WORLD_PX = 512;

/** Where Web Mercator stops. Latitudes beyond it project to infinity. */
const MAX_LAT = 85.051129;

export interface Clusterable {
  readonly lat: number;
  readonly lon: number;
}

export interface Cluster<T> {
  /** The centre of the marks it stands for, not of the cell that caught them. */
  readonly lon: number;
  readonly lat: number;
  readonly count: number;
  /** [west, south, east, north] — what a click fits the camera to. */
  readonly bounds: readonly [number, number, number, number];
  readonly members: readonly T[];
}

export interface Clustered<T> {
  readonly clusters: readonly Cluster<T>[];
  /** Everything still drawn as itself, in the order it arrived. */
  readonly loose: readonly T[];
}

/**
 * Which grid the map is clustering on right now.
 *
 * Quantised to whole zooms, exactly as MapLibre's own clustering is: it builds
 * one tree per integer zoom and reads the nearest. That is what makes the
 * behaviour cheap — a pinch from 10.0 to 10.9 changes nothing and needs no
 * recompute — and what makes it legible, because clusters then split at a
 * moment rather than dissolving continuously under the reader's fingers.
 */
export function clusterZoom(zoom: number): number {
  return Math.floor(zoom);
}

function projectX(lon: number, size: number): number {
  return ((lon + 180) / 360) * size;
}

function projectY(lat: number, size: number): number {
  const clamped = Math.min(MAX_LAT, Math.max(-MAX_LAT, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size;
}

function unprojectX(x: number, size: number): number {
  return (x / size) * 360 - 180;
}

function unprojectY(y: number, size: number): number {
  // The Gudermannian, the inverse of the log-sine above.
  return (Math.atan(Math.sinh(2 * Math.PI * (0.5 - y / size))) * 180) / Math.PI;
}

/**
 * Group points that crowd each other at a given zoom.
 *
 * Greedy agglomeration over a grid whose cell is the radius, which is
 * supercluster's own shape: a seed takes everything unclaimed within a radius
 * of it, and because the cell equals the radius those candidates can only be
 * in the nine cells around the seed's own. Linear in the number of points, and
 * no boundary artefact — a plain grid bucket would leave two marks a metre
 * apart uncounted for having fallen either side of a cell edge.
 *
 * Seeded in a sorted order rather than in the caller's, so the same input
 * clusters the same way whatever order the poll delivered it in. A works that
 * moved to the front of the feed must not reshuffle every bubble on the map.
 *
 * Scotland is nowhere near the antimeridian, so nothing here wraps at ±180.
 */
export function clusterPoints<T extends Clusterable>(
  points: readonly T[],
  zoom: number,
  options: { radiusPx?: number; maxZoom?: number; minSize?: number } = {},
): Clustered<T> {
  const radius = options.radiusPx ?? CLUSTER_RADIUS_PX;
  const maxZoom = options.maxZoom ?? CLUSTER_MAX_ZOOM;
  const minSize = Math.max(2, options.minSize ?? CLUSTER_MIN);
  const z = clusterZoom(zoom);

  if (z >= maxZoom || points.length < minSize) {
    return { clusters: [], loose: points.slice() };
  }

  const size = WORLD_PX * 2 ** z;
  const xs = new Float64Array(points.length);
  const ys = new Float64Array(points.length);
  const cells = new Map<string, number[]>();
  const order: number[] = [];

  for (let i = 0; i < points.length; i += 1) {
    xs[i] = projectX(points[i]!.lon, size);
    ys[i] = projectY(points[i]!.lat, size);
    const key = `${Math.floor(xs[i]! / radius)},${Math.floor(ys[i]! / radius)}`;
    const cell = cells.get(key);
    if (cell) cell.push(i);
    else cells.set(key, [i]);
    order.push(i);
  }
  order.sort((a, b) => xs[a]! - xs[b]! || ys[a]! - ys[b]! || a - b);

  const claimed = new Uint8Array(points.length);
  const collapsed = new Uint8Array(points.length);
  const clusters: Cluster<T>[] = [];

  for (const seed of order) {
    if (claimed[seed]) continue;
    const cx = Math.floor(xs[seed]! / radius);
    const cy = Math.floor(ys[seed]! / radius);
    const group: number[] = [];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (const j of cells.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (claimed[j]) continue;
          if (Math.hypot(xs[j]! - xs[seed]!, ys[j]! - ys[seed]!) > radius) continue;
          group.push(j);
        }
      }
    }
    // Claimed whether or not the group is big enough to draw as one. A mark
    // this seed reached but could not use must not then be swept into a
    // cluster centred somewhere it is not — the bubble would sit further from
    // it than the radius the count claims to cover.
    for (const j of group) claimed[j] = 1;
    if (group.length < minSize) continue;

    group.sort((a, b) => a - b);
    let sx = 0;
    let sy = 0;
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    const members: T[] = [];
    for (const j of group) {
      collapsed[j] = 1;
      sx += xs[j]!;
      sy += ys[j]!;
      const p = points[j]!;
      if (p.lon < west) west = p.lon;
      if (p.lon > east) east = p.lon;
      if (p.lat < south) south = p.lat;
      if (p.lat > north) north = p.lat;
      members.push(p);
    }
    clusters.push({
      lon: unprojectX(sx / group.length, size),
      lat: unprojectY(sy / group.length, size),
      count: group.length,
      bounds: [west, south, east, north],
      members,
    });
  }

  const loose: T[] = [];
  for (let i = 0; i < points.length; i += 1) if (!collapsed[i]) loose.push(points[i]!);
  return { clusters, loose };
}

/**
 * Where the camera should land when a cluster is clicked.
 *
 * `fitted` is what fitting the cluster's own bounds would give, which is the
 * right answer for a cluster spread over a few streets and no answer at all
 * for one whose marks share a coordinate: those bounds have no extent, and
 * fitting them sends the camera to the map's own maximum zoom — a jump to
 * rooftop height that separates nothing, because coincident marks stay
 * coincident.
 *
 * So it is capped at the zoom where clustering stops. Past that the marks draw
 * as themselves whatever happens next, and one click has done all it can. And
 * it is floored a whole level above where the reader is, so a click always
 * visibly moves: a wide cluster can otherwise fit inside the viewport at
 * almost the zoom it was clicked from, which reads as a click that did
 * nothing.
 */
export function clusterClickZoom(
  current: number,
  fitted: number | undefined,
  maxZoom: number = CLUSTER_MAX_ZOOM,
): number {
  return Math.max(Math.min(fitted ?? maxZoom, maxZoom), current + 1);
}
